import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { socialExport } from '@mi-front/api-clients';
import { QueryView } from '@mi-front/ui';
import { Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

interface ConnectionProfileDisplayProps {
  stationId: string;
  platform: SocialPlatform;
}

/** Scale factor for the chip to make it visually "large". */
const CHIP_SCALE = 1.5;

const largeChipSx = {
  height: 32 * CHIP_SCALE,
  borderRadius: 16 * CHIP_SCALE,
  '& .MuiChip-label': {
    paddingRight: 4 * CHIP_SCALE,
    paddingLeft: 4 * CHIP_SCALE,
    fontSize: `${0.8125 * CHIP_SCALE}rem`,
  },
  '& .MuiChip-avatar': {
    width: 24 * CHIP_SCALE,
    height: 24 * CHIP_SCALE,
    fontSize: `${0.75 * CHIP_SCALE}rem`,
  },
};

/**
 * Displays the profile/connection that will be used for uploads for the given station and platform.
 * Uses a large chip with the connection avatar or the social platform icon when the avatar image is not available.
 */
export function ConnectionProfileDisplay({ stationId, platform }: ConnectionProfileDisplayProps) {
  const connectionQuery = socialExport.useGetConnection(stationId, platform.id, {
    query: { enabled: !!stationId },
  });

  return (
    <QueryView query={connectionQuery} inline>
      {(state: socialExport.GetConnection200) => {
        const hasPhoto = !!state.profile.photo;

        return (
          <Stack sx={{ gap: 1 }}>
            <Typography variant="subtitle2">
              <FormattedMessage
                id="socialExport.profile.publicationAccount"
                defaultMessage="Cuenta de publicación"
              />
            </Typography>
            <Box>
              <Chip
                avatar={
                  <Avatar
                    src={hasPhoto ? state.profile.photo : undefined}
                    slotProps={{
                      img: {
                        crossOrigin: 'anonymous',
                      },
                    }}
                  >
                    {!hasPhoto ? platform.Icon({ colored: true }) : null}
                  </Avatar>
                }
                label={state.profile.displayName}
                sx={largeChipSx}
              />
            </Box>
          </Stack>
        );
      }}
    </QueryView>
  );
}
